/**
 * Client-side fallback for snippets/breadcrumb.liquid.
 *
 * Rendered only when the Liquid runtime couldn't parse the menu tree
 * server-side (parse_json unsupported). Builds the identical breadcrumb
 * trail in the browser: reads the menu tree from the mega menu's inline
 * JSON (<script data-mh-mm-tree>) when present, otherwise fetches
 * assets/menu-tree.json. Matching rules mirror the Liquid walk exactly:
 * the visited collection wins, then the deepest node matching any of the
 * product's collections, and non-product pages match on the current path.
 */

const MAX_DEPTH = 5;

/**
 * Normalizes a URL to a path-only form, matching snippets/menu-tree-url-path.liquid.
 * @param {string} url
 * @returns {string}
 */
function normalizePath(url) {
  if (!url) return '';
  let path = String(url);
  if (path.includes('://')) {
    const tail = path.split('://')[1];
    const host = tail.split('/')[0];
    path = path.replace(`https://${host}`, '').replace(`http://${host}`, '');
  }
  return path.split('?')[0].split('#')[0];
}

class MhBreadcrumbFallback extends HTMLElement {
  async connectedCallback() {
    const tree = this.#inlineTree() ?? (await this.#fetchTree());
    const items = tree?.items;
    if (!Array.isArray(items) || items.length === 0) return;

    const isProduct = this.hasAttribute('data-product');
    const currentPath = this.dataset.currentPath || '';
    const visitedPath = this.dataset.visitedPath || '';
    const candidates = new Set((this.dataset.candidates || '').split('|').filter(Boolean));

    /** @type {Array<{ title?: string, url?: string }> | null} */
    let visitedTrail = null;
    let bestTrail = null;
    let bestLevel = 0;
    let exactTrail = null;

    const walk = (nodes, trail, level) => {
      if (!Array.isArray(nodes) || level > MAX_DEPTH) return;
      for (const node of nodes) {
        if (visitedTrail || exactTrail) return;

        const nodeTrail = [...trail, node];
        const path = normalizePath(node.url || '');

        if (path) {
          if (isProduct) {
            if (visitedPath && path === visitedPath) {
              visitedTrail = nodeTrail;
              return;
            }
            if (candidates.has(path) && level > bestLevel) {
              bestLevel = level;
              bestTrail = nodeTrail;
            }
          } else if (path === currentPath) {
            exactTrail = nodeTrail;
            return;
          }
        }

        walk(node.children, nodeTrail, level + 1);
      }
    };
    walk(items, [], 1);

    const trail = isProduct ? visitedTrail || bestTrail : exactTrail;
    if (!trail) return;

    this.#render(trail, isProduct);
  }

  /**
   * Renders the trail with the same markup and schema.org microdata as the
   * server-side path in snippets/breadcrumb.liquid.
   *
   * @param {Array<{ title?: string, url?: string }>} trail
   * @param {boolean} isProduct
   */
  #render(trail, isProduct) {
    const nav = document.createElement('nav');
    nav.className = 'mh-breadcrumb';
    nav.setAttribute('aria-label', this.dataset.ariaLabel || 'Breadcrumbs');

    const list = document.createElement('ol');
    list.className = 'mh-breadcrumb__list list-unstyled';
    list.setAttribute('itemscope', '');
    list.setAttribute('itemtype', 'https://schema.org/BreadcrumbList');

    let position = 0;

    const addCrumb = (name, url, isCurrent) => {
      position += 1;
      const item = document.createElement('li');
      item.className = `mh-breadcrumb__item${isCurrent ? ' mh-breadcrumb__item--current' : ''}`;
      item.setAttribute('itemprop', 'itemListElement');
      item.setAttribute('itemscope', '');
      item.setAttribute('itemtype', 'https://schema.org/ListItem');

      if (position > 1) {
        const separator = document.createElement('span');
        separator.className = 'mh-breadcrumb__separator';
        separator.setAttribute('aria-hidden', 'true');
        separator.textContent = '/';
        item.appendChild(separator);
      }

      if (!isCurrent && url) {
        const link = document.createElement('a');
        link.className = 'mh-breadcrumb__link';
        link.href = url;
        link.setAttribute('itemprop', 'item');
        const label = document.createElement('span');
        label.setAttribute('itemprop', 'name');
        label.textContent = name;
        link.appendChild(label);
        item.appendChild(link);
      } else {
        const current = document.createElement('span');
        current.className = 'mh-breadcrumb__current';
        current.setAttribute('itemprop', 'name');
        if (isCurrent) current.setAttribute('aria-current', 'page');
        current.textContent = name;
        item.appendChild(current);
      }

      const meta = document.createElement('meta');
      meta.setAttribute('itemprop', 'position');
      meta.setAttribute('content', String(position));
      item.appendChild(meta);

      list.appendChild(item);
    };

    addCrumb(this.dataset.homeLabel || 'Home', this.dataset.rootUrl || '/', false);

    trail.forEach((node, index) => {
      const isLastNode = index === trail.length - 1;
      const isCurrent = isLastNode && !isProduct;
      addCrumb(node.title || '', normalizePath(node.url || ''), isCurrent);
    });

    if (isProduct) {
      addCrumb(this.dataset.productTitle || '', '', true);
    }

    this.replaceChildren(nav);
  }

  /**
   * Reads the menu tree from the mega menu's inline JSON, if the header
   * rendered one on this page (locale-correct, no network request).
   */
  #inlineTree() {
    const script = document.querySelector('script[data-mh-mm-tree]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '');
    } catch (parseError) {
      return null;
    }
  }

  /**
   * Fetches the menu tree asset as a last resort.
   */
  async #fetchTree() {
    const url = this.dataset.treeUrl;
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      return await response.json();
    } catch (fetchError) {
      return null;
    }
  }
}

if (!customElements.get('mh-breadcrumb-fallback')) {
  customElements.define('mh-breadcrumb-fallback', MhBreadcrumbFallback);
}
